import econLogoUrl from "../skin/brand/econ-logo.png?url";
const CLOCK_REFRESH_MS = 15_000;
/**
 * Port of the original skin's `.site-footer.nothing-footer` / `.nf-*` footer chrome
 * (`SKIN/index.html` lines 123-157, `#siteFooter`) plus the clock logic from
 * `SKIN/core/shell/chrome/footer-clock.js`, rewritten as a small self-contained TS timer.
 *
 * epic #1507 fidelity fix: the root element is missing the `nothing-footer` class the
 * vendored `legacy-numbered.css` scopes every `.nf-*` rule under (`.nothing-footer .nf-
 * toprow`, `.nothing-footer .nf-dot--y`, etc. — a bare `.site-footer` alone matches none of
 * them), so the whole footer was rendering unstyled. Also missing: the `.nf-econ-logo`
 * (first child of `.nf-bottomrow`, `SKIN/index.html` line 149) and two of the original's
 * four footer links — the source has TERMS OF SERVICE / PRIVACY POLICY / INSTAGRAM / X
 * (`SKIN/index.html` lines 151-154), not just the first two.
 *
 * Clock refresh: driven by `requestAnimationFrame` against a `dueAt` timestamp rather than
 * `setInterval` — same rationale as `stages/waves-stage.ts`'s typing engine (boundary rule
 * C1: no ad-hoc recurring timers; `client/ui` cannot reach `client/kernel`'s `KernelScheduler`
 * per boundary rule B2, so view-local recurring work follows the `requestAnimationFrame`-loop
 * convention already used by `stages/waves-canvas.ts`).
 */
export class NodeShellFooterElement extends HTMLElement {
    #clockRafId;
    #clockDueAt = 0;
    connectedCallback() {
        this.render();
        this.#tickClock();
        this.#clockDueAt = performance.now() + CLOCK_REFRESH_MS;
        this.#clockRafId = requestAnimationFrame(this.#stepClock);
        this.addEventListener("click", this.#handleClick);
    }
    disconnectedCallback() {
        if (this.#clockRafId !== undefined) {
            cancelAnimationFrame(this.#clockRafId);
            this.#clockRafId = undefined;
        }
        this.removeEventListener("click", this.#handleClick);
    }
    render() {
        this.innerHTML = `
      <footer class="site-footer nothing-footer" aria-label="Page footer">
        <div class="nf-divider nf-divider--top" aria-hidden="true"></div>
        <div class="nf-toprow">
          <div class="nf-clock" data-nf-clock aria-label="Current date and time">0000/00/00 ( 00 : 00 )</div>
        </div>
        <div class="nf-statement-row">
          <div class="nf-statement">Redemocratizing the internet</div>
          <div class="nf-dots" aria-hidden="true">
            <span class="nf-dot nf-dot--y"></span>
            <span class="nf-dot nf-dot--w"></span>
            <span class="nf-dot nf-dot--k"></span>
            <span class="nf-dot nf-dot--m"></span>
          </div>
        </div>
        <div class="nf-divider" aria-hidden="true"></div>
        <div class="nf-bottomrow">
          <img class="nf-econ-logo" src="${econLogoUrl}" alt="Economy V1" draggable="false">
          <nav class="nf-links" aria-label="Footer links">
            <a class="nf-link" href="#" data-nf-link>TERMS OF SERVICE</a>
            <a class="nf-link" href="#" data-nf-link>PRIVACY POLICY</a>
            <a class="nf-link" href="#" data-nf-link>INSTAGRAM</a>
            <a class="nf-link" href="#" data-nf-link>X</a>
          </nav>
        </div>
      </footer>
    `;
    }
    #handleClick = (event) => {
        const target = event.target;
        if (target instanceof HTMLElement && target.closest("[data-nf-link]")) {
            // Placeholder legal/social links from the source skin: no destination page exists
            // yet, so suppress the default `#` navigation instead of jumping/scrolling.
            event.preventDefault();
        }
    };
    #stepClock = (now) => {
        if (now < this.#clockDueAt) {
            this.#clockRafId = requestAnimationFrame(this.#stepClock);
            return;
        }
        this.#tickClock();
        this.#clockDueAt = now + CLOCK_REFRESH_MS;
        this.#clockRafId = requestAnimationFrame(this.#stepClock);
    };
    #tickClock() {
        const clock = this.querySelector("[data-nf-clock]");
        if (!clock) {
            return;
        }
        const now = new Date();
        const pad = (value) => String(value).padStart(2, "0");
        clock.textContent = `${now.getFullYear()}/${pad(now.getMonth() + 1)}/${pad(now.getDate())}`
            + ` ( ${pad(now.getHours())} : ${pad(now.getMinutes())} )`;
    }
}
//# sourceMappingURL=footer.js.map